import { type ReactNode, useEffect } from 'react'

// ============ Layout ============

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5 sm:mb-6">
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-neutral-900">{title}</h1>
        {subtitle && <p className="text-[13px] sm:text-sm text-neutral-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 shrink-0">{actions}</div>}
    </div>
  )
}

// ============ Button ============

type BtnProps = {
  children: ReactNode
  onClick?: (e: React.MouseEvent) => void
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  disabled?: boolean
  type?: 'button' | 'submit'
  className?: string
  title?: string
}
export function Button({ children, onClick, variant = 'primary', size = 'md', disabled, type = 'button', className = '', title }: BtnProps) {
  const base = 'inline-flex items-center justify-center gap-1.5 font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20 focus-visible:ring-offset-1'
  const sizes: Record<string, string> = {
    sm: 'h-7 px-2.5 text-[12px]',
    md: 'h-9 px-3.5 text-[13px]',
  }
  const variants: Record<string, string> = {
    primary:   'bg-neutral-900 text-white hover:bg-neutral-800 active:bg-black',
    secondary: 'bg-white border border-neutral-200 text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100',
    ghost:     'text-neutral-600 hover:bg-neutral-100 active:bg-neutral-200',
    danger:    'text-red-600 hover:bg-red-50 active:bg-red-100',
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}>
      {children}
    </button>
  )
}

// ============ Card ============

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`bg-white border border-neutral-200 rounded-xl ${className}`}>{children}</div>
}

// ============ Table (адаптивная: карточки на мобилке) ============

type Col<T> = {
  key: string
  label: string
  render: (r: T) => ReactNode
  w?: string
  hideOnMobile?: boolean
  mobileLabel?: string
}

export function Table<T>({ cols, rows, empty, onRowClick }: { cols: Col<T>[]; rows: T[]; empty?: string; onRowClick?: (row: T) => void }) {
  return (
    <>
      {/* Desktop-таблица */}
      <div className="hidden md:block">
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/60">
                {cols.map((c) => (
                  <th key={c.key}
                      className="text-left font-medium text-neutral-500 text-[11px] uppercase tracking-wider px-4 py-2.5"
                      style={c.w ? { width: c.w } : {}}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-neutral-400 text-sm">{empty || 'Нет данных.'}</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i}
                    onClick={onRowClick ? (e) => {
                      // не тригерим если клик по кнопке/ссылке/input
                      const el = e.target as HTMLElement
                      if (el.closest('button,a,input,textarea,select,label')) return
                      onRowClick(r)
                    } : undefined}
                    className={`border-b border-neutral-100 last:border-0 transition-colors ${
                      onRowClick ? 'hover:bg-neutral-50 cursor-pointer' : 'hover:bg-neutral-50/60'
                    }`}>
                  {cols.map((c) => <td key={c.key} className="px-4 py-3 align-middle">{c.render(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Mobile-карточки */}
      <div className="md:hidden space-y-2">
        {rows.length === 0 && (
          <Card className="p-8 text-center text-sm text-neutral-400">{empty || 'Нет данных.'}</Card>
        )}
        {rows.map((r, i) => (
          <Card key={i} className="p-3.5 space-y-2">
            {cols.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-3">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 pt-0.5 shrink-0">
                  {c.mobileLabel ?? c.label}
                </div>
                <div className="text-right min-w-0 flex-1">{c.render(r)}</div>
              </div>
            ))}
          </Card>
        ))}
      </div>
    </>
  )
}

// ============ Badges ============

export function Badge({ children, tone = 'neutral' }: {
  children: ReactNode
  tone?: 'neutral' | 'green' | 'blue' | 'yellow' | 'red' | 'purple'
}) {
  const map: Record<string, string> = {
    neutral: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    green:   'bg-green-50 text-green-700 border-green-200',
    blue:    'bg-blue-50 text-blue-700 border-blue-200',
    yellow:  'bg-yellow-50 text-yellow-800 border-yellow-200',
    red:     'bg-red-50 text-red-700 border-red-200',
    purple:  'bg-purple-50 text-purple-700 border-purple-200',
  }
  return <span className={`inline-flex items-center px-1.5 py-0.5 text-[11px] leading-4 rounded border font-medium ${map[tone]}`}>{children}</span>
}

// ============ Modal (адаптивный, mobile bottom-sheet) ============

export function Modal({ open, onClose, title, children, size = 'md' }: {
  open: boolean; onClose: () => void; title: string; children: ReactNode; size?: 'sm' | 'md' | 'lg'
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [open, onClose])

  if (!open) return null
  const w = { sm: 'sm:max-w-md', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' }[size]

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
         onClick={onClose}>
      <div className={`bg-white w-full ${w} rounded-t-2xl sm:rounded-xl shadow-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col`}
           onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between shrink-0">
          <h3 className="font-semibold text-[15px]">{title}</h3>
          <button onClick={onClose}
                  className="text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 rounded-md w-8 h-8 flex items-center justify-center transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

// ============ Form controls ============

export function Field({ label, hint, children, className }: { label: string; hint?: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className || ''}`}>
      <div className="text-[12px] font-medium text-neutral-700 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-neutral-500 mt-1">{hint}</div>}
    </label>
  )
}

const inputCls = 'w-full h-9 border border-neutral-300 rounded-md px-3 text-[13px] bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 disabled:bg-neutral-50 disabled:text-neutral-500 placeholder:text-neutral-400'

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${inputCls} ${props.className || ''}`} />
}
export function Select({ children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return <select {...props} className={`${inputCls} pr-8 appearance-none bg-[url("data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20width='12'%20height='12'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23737373'%20stroke-width='2'%20stroke-linecap='round'%3e%3cpath%20d='M6%209l6%206%206-6'/%3e%3c/svg%3e")] bg-[length:12px] bg-[right_10px_center] bg-no-repeat ${props.className || ''}`}>{children}</select>
}
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`w-full min-h-[80px] border border-neutral-300 rounded-md px-3 py-2 text-[13px] bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 placeholder:text-neutral-400 ${props.className || ''}`} />
}
export function Checkbox({ label, ...props }: { label?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-[13px] text-neutral-800 select-none">
      <input type="checkbox" {...props}
             className="w-4 h-4 rounded border-neutral-300 text-neutral-900 focus:ring-2 focus:ring-neutral-900/10" />
      {label && <span>{label}</span>}
    </label>
  )
}

// ============ Empty / Loading / Error ============

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <Card className="p-10 text-center">
      <div className="text-[15px] font-medium text-neutral-800 mb-1">{title}</div>
      {hint && <div className="text-[13px] text-neutral-500 max-w-md mx-auto">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  )
}

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="animate-spin text-neutral-400">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.2"/>
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  )
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return <div className="text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{children}</div>
}
